/**
 * BlogTopicPage – öffentliche Detail-Seite eines Themas.
 *
 * Route: /blog/themen/:slug
 *
 * Zeigt: Hero-Bild, Titel, Anreißer, ausführlichen HTML-Content und
 * (optional) verwandte Produkte aus dem Shop.
 */
import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import axiosInstance from '../api/axios';
import Navbar from '../components/Navbar';

const BlogTopicPage = () => {
  const { slug } = useParams();
  const [topic, setTopic] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await axiosInstance.get(`/blog/topics/${slug}`);
        if (alive) setTopic(r.data);
      } catch (e) {
        if (alive) setError('Thema nicht gefunden');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAF5EE]">
        <Navbar />
        <div className="max-w-3xl mx-auto px-4 py-12 text-center text-[#5D4E3C]">
          Wird geladen…
        </div>
      </div>
    );
  }
  if (error || !topic) {
    return (
      <div className="min-h-screen bg-[#FAF5EE]">
        <Navbar />
        <div className="max-w-3xl mx-auto px-4 py-12 text-center">
          <p className="text-[#5D4E3C] text-lg mb-4">{error || 'Thema nicht gefunden'}</p>
          <Link to="/blog" className="text-[#C4A084] underline">Zurück zum Blog</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF5EE]" data-testid="blog-topic-page">
      <Navbar />

      <article className="max-w-3xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        <Link
          to="/blog"
          className="inline-flex items-center gap-2 text-sm text-[#8B7355] mb-4 hover:text-[#5D4E3C]"
          data-testid="blog-topic-back"
        >
          <ArrowLeft size={16} /> Zurück zum Blog
        </Link>

        {topic.image && (
          <div className="aspect-[16/9] w-full overflow-hidden mb-6 bg-white">
            <img
              src={topic.image}
              alt={topic.title}
              className="w-full h-full object-cover"
              data-testid="blog-topic-hero"
            />
          </div>
        )}

        <h1
          className="text-3xl sm:text-4xl font-bold text-[#3E2723] mb-3 leading-tight"
          data-testid="blog-topic-title"
        >
          {topic.title}
        </h1>

        {topic.excerpt && (
          <p className="text-lg text-[#5D4E3C] mb-8 leading-relaxed italic">
            {topic.excerpt}
          </p>
        )}

        {/* Rendered HTML content */}
        <div
          className="topic-content prose max-w-none text-[#3E2723] leading-relaxed"
          data-testid="blog-topic-content"
          dangerouslySetInnerHTML={{ __html: topic.content }}
        />

        {/* Related products */}
        {Array.isArray(topic.related_products) && topic.related_products.length > 0 && (
          <section className="mt-12 pt-8 border-t border-[#E8DCC8]">
            <h2 className="text-xl font-bold text-[#5D4E3C] mb-4">
              Passend dazu im Shop
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {topic.related_products.map((p) => (
                <Link
                  key={p.id}
                  to={`/products/${p.id}`}
                  className="block bg-white border border-[#E8DCC8] hover:border-[#C4A084] transition"
                  data-testid={`topic-related-product-${p.id}`}
                >
                  {p.images?.[0] && (
                    <div className="aspect-square bg-[#FAF5EF]">
                      <img src={p.images[0]} alt={p.name} className="w-full h-full object-contain p-2" />
                    </div>
                  )}
                  <div className="p-3">
                    <p className="text-sm font-medium text-[#3E2723] line-clamp-2">{p.name}</p>
                    <p className="text-sm font-bold text-[#C4A084] mt-1">{p.price?.toFixed?.(2)}€</p>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </article>

      <style>{`
        .topic-content h2 { font-size: 1.5rem; font-weight: 700; color: #5D4E3C; margin-top: 2rem; margin-bottom: 0.75rem; }
        .topic-content h3 { font-size: 1.2rem; font-weight: 600; color: #6F5947; margin-top: 1.5rem; margin-bottom: 0.5rem; }
        .topic-content p { margin: 0.5rem 0 1rem; line-height: 1.75; }
        .topic-content ul, .topic-content ol { margin: 0.5rem 0 1.25rem 1.5rem; }
        .topic-content li { margin-bottom: 0.5rem; line-height: 1.7; }
        .topic-content strong { color: #3E2723; }
        .topic-content img { max-width: 100%; height: auto; margin: 1.25rem 0; border: 1px solid #E8DCC8; }
      `}</style>
    </div>
  );
};

export default BlogTopicPage;
